import {getTestProps, type DataTestAttrs} from '@benchling/js-test-utils';
import {Card, Group, Text, createStyles, Flex, Center, Badge, Box} from '@mantine/core';
import {useId} from '@mantine/hooks';
import React from 'react';

import {isSimpleClick, navigate} from 'BenchlingUtil';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import {getActionButtonRoleProps} from 'lib/a11y/ButtonA11y';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import fuzzydate from 'util/fuzzydate';
import {ElementMouseEvent} from 'util/Types';

const useStyles = createStyles((theme) => {
  const rowDivider = {borderTop: `1px solid ${theme.colors.gray[2]}`};

  return {
    clickable: {
      cursor: 'pointer',
    },
    flexGrow: {
      flexGrow: 1,
    },
    flexStart: {
      alignSelf: 'flex-start',
    },
    cardSectionRowHover: {
      '&:hover': {
        backgroundColor: theme.colors.gray[1],
      },
    },
    cardSectionList: {
      '& > * + *': rowDivider,
      '&:not(:first-child) > :first-child': rowDivider,
    },
    linkReset: {
      display: 'block',
      '&, &:hover, &:visited, &:active, &:focus': {
        color: 'inherit',
        textDecoration: 'none',
      },
    },
    cardSectionRowIconWrapper: {
      width: '28px',
      height: '28px',
      backgroundColor: theme.colors.gray[2],
      marginRight: theme.spacing.sm,
    },
    cardSectionMessageRowClickable: {
      ...rowDivider,
      '&:hover': {
        textDecoration: 'underline',
      },
    },
  };
});

/** Card layout primitive for homepage sections (title, optional header actions, bordered body). */
export default function CardSection({
  title,
  rightContent,
  children,
  dataTestAttrs,
}: {
  title: React.ReactNode;
  rightContent?: React.ReactNode;
  children: React.ReactNode;
  dataTestAttrs: DataTestAttrs;
}): JSX.Element {
  const titleId = useId();
  const titleDataTestAttrs = {component: dataTestAttrs.component, element: 'title'};

  return (
    <div role="region" aria-labelledby={titleId} {...getTestProps(dataTestAttrs)}>
      <Group position="apart" p="md">
        <Text size="xl" id={titleId} {...getTestProps(titleDataTestAttrs)}>
          {title}
        </Text>
        {rightContent}
      </Group>
      <Card radius="lg" withBorder={true} p="0">
        {children}
      </Card>
    </div>
  );
}

/** Wraps {@link CardSectionRow} siblings with `role="list"`. */
export function CardSectionRowList({children}: {children: React.ReactNode}): JSX.Element {
  const {classes} = useStyles();

  return (
    <Box role="list" className={classes.cardSectionList}>
      {children}
    </Box>
  );
}

export interface CardSectionRowProps {
  id: string;
  name: string;
  modifiedAt?: string;
  icon?: BenchlingIconName;
  count?: number;
  href?: string;
  onClick?: () => void;
  rightContent?: React.ReactNode;
  /**
   * When set, adds stable data-test attributes on the list item and its interactive target.
   * Use {@code actionRow} to click rows whose target may render as a link or button.
   */
  dataTestComponent?: string;
}

function CardSectionRowContent({
  icon,
  name,
  modifiedAt,
  count,
  rightContent,
}: Pick<CardSectionRowProps, 'icon' | 'name' | 'modifiedAt' | 'count' | 'rightContent'>): JSX.Element {
  const {classes, cx} = useStyles();

  return (
    <Flex align="center" p="10px 16px">
      {icon && (
        <Center className={cx(classes.cardSectionRowIconWrapper, classes.flexStart)}>
          <BenchlingIcon icon={icon} style={{height: '16px', width: '16px'}} />
        </Center>
      )}
      <Group>
        <Text fz="lg" component="span">
          {name}
        </Text>
        {modifiedAt != null && (
          <Text color="dimmed" fz="sm">
            Updated {fuzzydate(modifiedAt, {showTime: false})}
          </Text>
        )}
      </Group>
      {count ? (
        <Badge size="xl" radius="xl" p="8px" ml="md">
          {count}
        </Badge>
      ) : null}
      <div className={classes.flexGrow} />
      <Text color="dimmed" ta="right" component="span">
        {rightContent}
      </Text>
    </Flex>
  );
}

export function CardSectionRow({
  id,
  icon,
  name,
  modifiedAt,
  count,
  href,
  onClick,
  rightContent,
  dataTestComponent,
}: CardSectionRowProps): JSX.Element {
  const {classes} = useStyles();

  const rowTestAttrs =
    dataTestComponent != null ? {component: dataTestComponent, element: 'row' as const, key: id} : null;
  const actionRowTestAttrs =
    dataTestComponent != null ? {component: dataTestComponent, element: 'actionRow' as const, key: id} : null;

  const rowContent = (
    <CardSectionRowContent
      icon={icon}
      name={name}
      modifiedAt={modifiedAt}
      count={count}
      rightContent={rightContent}
    />
  );

  let rowBody: React.ReactNode = rowContent;
  if (href != null) {
    rowBody = (
      <Box
        component="a"
        href={href}
        className={classes.linkReset}
        onClick={(e: ElementMouseEvent) => {
          navigate(e, href);
          if (isSimpleClick(e)) {
            onClick?.();
          }
        }}
        {...(actionRowTestAttrs != null ? getTestProps(actionRowTestAttrs) : {})}
      >
        {rowContent}
      </Box>
    );
  } else if (onClick != null) {
    rowBody = (
      <Box
        className={classes.clickable}
        {...getActionButtonRoleProps({onPress: onClick})}
        {...(actionRowTestAttrs != null ? getTestProps(actionRowTestAttrs) : {})}
      >
        {rowContent}
      </Box>
    );
  }

  return (
    <Box
      role="listitem"
      className={classes.cardSectionRowHover}
      {...(rowTestAttrs != null ? getTestProps(rowTestAttrs) : {})}
    >
      {rowBody}
    </Box>
  );
}

export function CardSectionMessageRow({
  message,
  onClick,
  dataTestAttrs,
}: {
  message: string;
  onClick?: () => void;
  dataTestAttrs?: DataTestAttrs;
}): JSX.Element {
  const {classes, cx} = useStyles();

  return (
    <Box
      className={cx(onClick && classes.clickable, onClick && classes.cardSectionMessageRowClickable)}
      {...(onClick && getActionButtonRoleProps({onPress: onClick}))}
      {...getTestProps(dataTestAttrs ?? {})}
    >
      <Flex align="center" p="10px 16px">
        <Text color={onClick ? 'blue' : 'dimmed'} fz="lg">
          {message}
        </Text>
      </Flex>
    </Box>
  );
}

/** Renders a {@link CardSectionMessageRow} for a loading message (e.g. while parent data is not ready). */
export function renderQueryLoadingMessage(message: string, dataTestComponent: string): JSX.Element {
  return (
    <CardSectionMessageRow
      message={message}
      dataTestAttrs={{component: dataTestComponent, element: 'messageRow', key: 'loading'}}
    />
  );
}

/** Renders a {@link CardSectionMessageRow} for loading or error query states; otherwise `null`. */
export function renderQueryLoadingOrError(
  queryResult: {type: QueryResultType},
  messages: {loading: string; error: string},
  dataTestComponent: string
): JSX.Element | null {
  if (queryResult.type === QueryResultType.PENDING) {
    return (
      <CardSectionMessageRow
        message={messages.loading}
        dataTestAttrs={{component: dataTestComponent, element: 'messageRow', key: 'loading'}}
      />
    );
  }
  if (queryResult.type === QueryResultType.ERROR) {
    return (
      <CardSectionMessageRow
        message={messages.error}
        dataTestAttrs={{component: dataTestComponent, element: 'messageRow', key: 'error'}}
      />
    );
  }
  return null;
}
